import React, { forwardRef, ReactElement, useCallback } from "react";
import TextInput from "../TextInput/TextInput";
import { ITextInputProps } from "../TextInput";
import useWatchState from "../../../../hooks/useWatchState";
import InputAdornment from "../InputAdornment/InputAdornment";
import { PasswordRevealIcon, PasswordHideIcon } from "../../../../icons";

export interface ISecureInputProps extends ITextInputProps {
  /**
   * Show content by default or not
   */
  visible?: boolean;
  /**
   * Allows secure content to be toggled / shown
   */
  allowToggle?: boolean;
  /**
   * An element to be shown as "show icon", for example, an eye
   */
  showItemElement?: ReactElement;
  /**
   * An element to be shown as "hide icon", for example, a crossed eye
   */
  hideItemElement?: ReactElement;
  /**
   * Callback triggered when content's visibility changes
   */
  onToggle?(visible: boolean): void;
}

const SecureInput = forwardRef<HTMLInputElement, ISecureInputProps>(
  (
    {
      allowToggle = true,
      showItemElement,
      hideItemElement,
      type = "text",
      visible = false,
      onToggle,
      endAdornment,
      value,
      ...props
    },
    ref = null,
  ) => {
    const [currentVisible, setCurrentVisible] = useWatchState({
      value: visible,
    });

    const handleToggleClick = useCallback(() => {
      if (allowToggle) {
        setCurrentVisible(!currentVisible);
        onToggle && onToggle(currentVisible);
      }
    }, [allowToggle, currentVisible, onToggle, setCurrentVisible]);

    const rightIconContent = useCallback(
      (injectedProps) => {
        if (endAdornment) {
          return endAdornment(injectedProps);
        }
        return value && allowToggle ? (
          <InputAdornment
            {...injectedProps}
            position="end"
            clickable
            onClick={handleToggleClick}
          >
            <>
              {currentVisible &&
                (hideItemElement || (
                  <PasswordHideIcon data-testid="SecureInput__hideIcon" />
                ))}
              {!currentVisible &&
                (showItemElement || (
                  <PasswordRevealIcon data-testid="SecureInput__showIcon" />
                ))}
            </>
          </InputAdornment>
        ) : null;
      },
      [
        allowToggle,
        currentVisible,
        endAdornment,
        handleToggleClick,
        hideItemElement,
        showItemElement,
        value,
      ],
    );

    return (
      <TextInput
        {...props}
        value={value}
        ref={ref}
        type={currentVisible ? type : "password"}
        endAdornment={rightIconContent}
      />
    );
  },
);

export default SecureInput;
